import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import crossFetch from 'cross-fetch';
import {
  TeamRoleEnum,
  TeamRolesApi,
} from '@agilelab/plugin-wb-team-roles-common';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';
import { NotAllowedError, NotFoundError } from '@backstage/errors';

export interface AccessControlAuthorizationServiceOptions {
  discoveryApi: { getBaseUrl(pluginId: string): Promise<string> };
  fetchApi?: { fetch: typeof fetch };
  teamRolesApi: TeamRolesApi;
}

const UNAUTHORIZED_MESSAGE =
  'User is not authorized to manage access requests for this product. Please contact its Owner or Data Access Manager.';

/**
 * Service for access-control authorization checks.
 * as {@link AccessControlListClient}.
 */
export class AccessControlAuthorizationService {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly teamRolesApi: TeamRolesApi;

  constructor(options: AccessControlAuthorizationServiceOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch: crossFetch };
    this.teamRolesApi = options.teamRolesApi;
  }

  /**
   * Asserts that the caller is authorized to manage access requests for a product.
   *
   * **Regime A** — Team roles enabled and project is onboarded:
   *   The caller must appear in the OWNER or DATA_ACCESS_MANAGER subjects
   *   (including limited assignees).
   *
   * **Regime B** — Team roles disabled or project is not onboarded yet:
   *   Falls back to the marketplace owner check — the caller must match the
   *   product's marketplace `owner` field.
   *
   * @param productInstanceId - The marketplace product instance ID (unique).
   * @param callerRef - The entity ref of the calling user.
   * @param options - Optional per-plugin tokens for service-to-service auth.
   * @throws NotAllowedError if the caller is not authorized.
   */
  async assertCanManageAccessRequests(
    productInstanceId: number,
    callerRef: string,
    options?: { marketplaceToken?: string; teamRolesToken?: string },
  ): Promise<void> {
    // Step 1: Fetch externalId and owner in parallel (both only need instanceId)
    const marketplaceOptions = options?.marketplaceToken
      ? { token: options.marketplaceToken }
      : undefined;
    const [externalId, marketplaceOwner] = await Promise.all([
      this.getProductExternalId(productInstanceId, marketplaceOptions),
      this.getProductOwner(productInstanceId, marketplaceOptions),
    ]);

    if (!externalId) {
      throw new NotFoundError(
        `Product instance ${productInstanceId} not found`,
      );
    }

    // Step 2: Resolve OWNER and DAM team roles in parallel (need externalId)
    const teamRolesOptions = options?.teamRolesToken
      ? { token: options.teamRolesToken }
      : undefined;
    const [dataAccessManagers, projectOwners] = await Promise.all([
      this.teamRolesApi.resolve({
        teamRole: TeamRoleEnum.DATA_ACCESS_MANAGER,
        projectUrn: externalId,
        mode: 'exact-match',
        useFallback: false,
        options: teamRolesOptions,
      }),
      this.teamRolesApi.resolve({
        teamRole: TeamRoleEnum.OWNER,
        projectUrn: externalId,
        mode: 'exact-match',
        /**
         * don't use catalog-info fallback,
         * if there is no owner directly assigned the project was not onboarded yet;
         * marketplace owner becomes the source of truth instead
         */
        useFallback: false,
        options: teamRolesOptions,
      }),
    ]);

    const projectOnboarded =
      projectOwners.isFeatureEnabled &&
      projectOwners.subjects.some(s => !s.isFallback && !s.isLimited);

    if (projectOwners.isFeatureEnabled && projectOnboarded) {
      // Regime A: TeamRoles active and project onboarded — check team role subjects
      const isAuthorized =
        dataAccessManagers.subjects.some(
          ({ subject }) => expandEntityRef(subject) === callerRef,
        ) ||
        projectOwners.subjects.some(
          ({ subject }) => expandEntityRef(subject) === callerRef,
        );

      if (!isAuthorized) {
        throw new NotAllowedError(UNAUTHORIZED_MESSAGE);
      }
    } else {
      // Regime B: TeamRoles is not enabled/onboarded - fallback to marketplace deployed instance owner
      if (
        !marketplaceOwner ||
        expandEntityRef(marketplaceOwner) !== callerRef
      ) {
        throw new NotAllowedError(UNAUTHORIZED_MESSAGE);
      }
    }
  }

  private async getProductExternalId(
    instanceId: number,
    options?: { token?: string },
  ): Promise<string | undefined> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/products/${instanceId}/external-id`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );

    if (response.status === 404) return undefined;
    if (!response.ok) {
      throw new Error(
        `Marketplace product external-id resolve failed: ${response.status} ${response.statusText}`,
      );
    }

    const body = await response.json();
    return body.externalId;
  }

  private async getProductOwner(
    instanceId: number,
    options?: { token?: string },
  ): Promise<string | undefined> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/products/${instanceId}/owner`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );

    if (response.status === 404) return undefined;
    if (!response.ok) {
      throw new Error(
        `Marketplace product owner resolve failed: ${response.status} ${response.statusText}`,
      );
    }

    const body = await response.json();
    return body.owner;
  }
}
